import React, {FC, forwardRef, useCallback, useMemo} from 'react';

import {cn} from '@bem-react/classname';
import {Button} from '@gravity-ui/uikit';
import MarkdownText from 'ui/components/MarkdownText';
import QuestionLayout, {QuestionLayoutType} from 'ui/components/QuestionLayout';

import {matrixQuestionPositions} from './MatrixQuestion.constants';
import {MatrixQuestionProps} from './MatrixQuestion.types';

import './MatrixQuestion.scss';

const cnMatrixQuestion = cn('MatrixQuestion');

const MatrixQuestion: FC<MatrixQuestionProps> = forwardRef<HTMLDivElement, MatrixQuestionProps>(
    function MatrixQuestion({rows, columns, value, onChange, name, ...props}, ref) {
        const emptyValue = useMemo(() => {
            const initialValue: Record<string, string> = {};
            rows.forEach(({id}) => {
                initialValue[id] = '';
            });
            return initialValue;
        }, [rows]);

        const handleClick = useCallback(
            (rowId: string, columnId: string) => () => {
                const updatedValue = Object.assign(emptyValue, value, {[rowId]: columnId});
                onChange(updatedValue);
            },
            [onChange, value, emptyValue],
        );

        return (
            <QuestionLayout
                {...props}
                type={QuestionLayoutType.Column}
                className={cnMatrixQuestion('', [props.className])}
                positions={matrixQuestionPositions}
                control={
                    <div className={cnMatrixQuestion('Control')} ref={ref}>
                        {rows.map((row) => (
                            <div
                                key={row.id}
                                className={cnMatrixQuestion('Row')}
                                role="radiogroup"
                                aria-required={props.required}
                                aria-labelledby={`${props.id}-${row.id}`}
                            >
                                <MarkdownText
                                    className={cnMatrixQuestion('RowTitle')}
                                    text={row.label}
                                    as={'h4'}
                                    id={`${props.id}-${row.id}`}
                                />
                                {columns.map((column) => {
                                    const selected = value[row.id] === column.id;
                                    return (
                                        <Button
                                            key={`${row.id}-${column.id}`}
                                            className={cnMatrixQuestion('Button', {selected})}
                                            size="l"
                                            view={selected ? 'normal' : 'outlined'}
                                            selected={selected}
                                            onClick={handleClick(row.id, column.id)}
                                            width="auto"
                                            extraProps={{
                                                name,
                                                role: 'radio',
                                                'aria-checked': selected,
                                            }}
                                            qa={`${name}_${row.id}_${column.id}`}
                                        >
                                            <MarkdownText
                                                text={column.label}
                                                className={cnMatrixQuestion('ButtonText')}
                                            />
                                        </Button>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                }
            />
        );
    },
);
export default MatrixQuestion;
